import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, PieChart, TrendingDown, Calendar } from 'lucide-react';
import { useTransactions, useCategories } from '../hooks';
import { calculateCategoryStats } from '../lib/services/StatisticsService';
import CategoryIconBadge from '../components/CategoryIconBadge';
import DonutChart from '../components/DonutChart';

interface CategoryExpense {
  name: string;
  amount: number;
  percentage: number;
  color: string;
  icon?: string | null;
  id: string;
}

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const Charts: React.FC = () => {
  const { transactions, loading: transactionsLoading } = useTransactions();
  const { categories, loading: categoriesLoading } = useCategories();
  
  // États pour la période
  const [viewMode, setViewMode] = useState<'month' | 'year'>('month');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [excludeTransfers, setExcludeTransfers] = useState(false); // Case à cocher "Ignorer les transferts" (décochée par défaut)
  
  // États pour les données
  const [categoryExpenses, setCategoryExpenses] = useState<CategoryExpense[]>([]);
  const [totalExpense, setTotalExpense] = useState(0);
  const [isCurrentMonth, setIsCurrentMonth] = useState(true);

  // Vérifier si on affiche le mois courant
  useEffect(() => {
    const now = new Date();
    setIsCurrentMonth(selectedMonth === now.getMonth() && selectedYear === now.getFullYear());
  }, [selectedMonth, selectedYear]);

  // Charger les données de dépenses
  useEffect(() => {
    if (!transactionsLoading && !categoriesLoading) {
      loadExpenseData();
    }
  }, [selectedMonth, selectedYear, viewMode, transactions, categories, transactionsLoading, categoriesLoading, excludeTransfers]);

  const loadExpenseData = async () => {
    try {
      setLoading(true);
      
      // Calculer les dates de début et fin selon le mode
      let startDate: string, endDate: string;
      
      if (viewMode === 'month') {
        startDate = new Date(selectedYear, selectedMonth, 1).toISOString().split('T')[0]!;
        endDate = new Date(selectedYear, selectedMonth + 1, 0).toISOString().split('T')[0]!;
      } else {
        startDate = new Date(selectedYear, 0, 1).toISOString().split('T')[0]!;
        endDate = new Date(selectedYear, 11, 31).toISOString().split('T')[0]!;
      }

      // Récupérer les statistiques par catégorie (avec option d'exclure les transferts)
      const { data: categoryStats, error } = await calculateCategoryStats(startDate, endDate, excludeTransfers);
      
      if (error) {
        console.error('Erreur lors du calcul des statistiques:', error);
        return;
      }

      if (categoryStats && categoryStats.length > 0) {
        // Convertir en format CategoryExpense
        const expenses = categoryStats.map(cat => ({
          name: cat.categorie_nom,
          amount: cat.total,
          percentage: cat.pourcentage,
          color: cat.categorie_couleur,
          icon: cat.categorie_icone,
          id: cat.categorie_id
        }));

        const total = expenses.reduce((sum, cat) => sum + cat.amount, 0);
        
        setCategoryExpenses(expenses);
        setTotalExpense(total);
      } else {
        setCategoryExpenses([]);
        setTotalExpense(0);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      if (selectedMonth === 0) {
        setSelectedMonth(11);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 11) {
        setSelectedMonth(0);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  const navigateYear = (direction: 'prev' | 'next') => {
    setSelectedYear(selectedYear + (direction === 'next' ? 1 : -1));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const getPeriodTitle = () => {
    if (viewMode === 'month') {
      return `${MONTHS[selectedMonth]} ${selectedYear}`;
    } else {
      return selectedYear.toString();
    }
  };

  // Données pour le graphique en camembert
  const chartData = categoryExpenses.length > 0 ? categoryExpenses : [
    {
      name: 'Aucune donnée',
      amount: 1,
      percentage: 100,
      color: '#666666',
      id: 'no-data'
    }
  ];

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div>
        <h1 className="text-3xl font-bold text-white">Graphiques</h1>
        <p className="text-dark-400 mt-2">
          Analysez vos dépenses par catégorie
        </p>
      </div>

      {/* Sélecteur de mode (Mois/Année) */}
      <div className="card">
        <div className="flex space-x-2 mb-6">
          <button
            onClick={() => setViewMode('month')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              viewMode === 'month'
                ? 'bg-primary-500 text-white'
                : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
            }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Mois
          </button>
          <button
            onClick={() => setViewMode('year')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              viewMode === 'year'
                ? 'bg-primary-500 text-white'
                : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
            }`}
          >
            <TrendingDown className="w-4 h-4 inline mr-2" />
            Année
          </button>
        </div>

        {/* Navigation entre les périodes */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => viewMode === 'month' ? navigateMonth('prev') : navigateYear('prev')}
            className="p-2 text-dark-400 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          <div className="text-center">
            <h2 className="text-xl font-bold text-white">{getPeriodTitle()}</h2>
            {isCurrentMonth && viewMode === 'month' && (
              <p className="text-sm text-green-400 mt-1">Mois en cours</p>
            )}
          </div>
          
          <button
            onClick={() => viewMode === 'month' ? navigateMonth('next') : navigateYear('next')}
            className="p-2 text-dark-400 hover:text-white transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Case à cocher "Ignorer les transferts" */}
        <div className="flex items-center space-x-2 mb-4">
          <input
            type="checkbox"
            id="excludeTransfers"
            checked={excludeTransfers}
            onChange={(e) => setExcludeTransfers(e.target.checked)}
            className="w-4 h-4 rounded border-dark-700 bg-dark-800 text-primary-500 focus:ring-primary-500 focus:ring-2"
          />
          <label htmlFor="excludeTransfers" className="text-white cursor-pointer">
            Ignorer les transferts
          </label>
        </div>
      </div>

      {loading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            <span className="text-white ml-4">Chargement des données...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Statistiques globales */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-dark-400 text-sm font-medium">Total des dépenses</p>
                  <p className="text-2xl font-bold text-white mt-1">
                    {formatCurrency(totalExpense)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-red-500" />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-dark-400 text-sm font-medium">Catégories</p>
                  <p className="text-2xl font-bold text-white mt-1">
                    {categoryExpenses.length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <PieChart className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-dark-400 text-sm font-medium">Moyenne par catégorie</p>
                  <p className="text-2xl font-bold text-white mt-1">
                    {categoryExpenses.length > 0 
                      ? formatCurrency(totalExpense / categoryExpenses.length)
                      : formatCurrency(0)
                    }
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-green-500" />
                </div>
              </div>
            </div>
          </div>

          {/* Graphique et détails */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Graphique en camembert */}
            <div className="card">
              <h3 className="text-lg font-semibold text-white mb-4">
                Répartition des dépenses
              </h3>
              
              {categoryExpenses.length === 0 ? (
                <div className="text-center py-12">
                  <PieChart className="w-16 h-16 text-dark-400 mx-auto mb-4" />
                  <p className="text-dark-400">Aucune dépense trouvée</p>
                  <p className="text-dark-400 text-sm mt-2">
                    Ajoutez des transactions pour voir les graphiques
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  <DonutChart
                    data={categoryExpenses}
                    size={280}
                    strokeWidth={28}
                    maxSlices={7}
                    centerPrimary={formatCurrency(totalExpense)}
                    centerSecondary="Total des dépenses"
                  />
                  <div className="space-y-1 divide-y divide-dark-700">
                    {chartData.map((category) => (
                      <div
                        key={category.id}
                        className="flex items-center gap-3 py-3 first:pt-0"
                      >
                        <CategoryIconBadge
                          color={category.color}
                          icon={category.icon}
                          size={40}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-white">{category.name}</p>
                          <div className="mt-1.5 flex items-center gap-2">
                            <div className="h-1 flex-1 overflow-hidden rounded-full bg-dark-800">
                              <div
                                className="h-1 rounded-full"
                                style={{
                                  width: `${Math.min(category.percentage, 100)}%`,
                                  backgroundColor: category.color,
                                }}
                              />
                            </div>
                            <span className="w-12 shrink-0 text-right text-xs text-dark-400">
                              {formatPercentage(category.percentage)}
                            </span>
                          </div>
                        </div>
                        <span className="shrink-0 font-bold text-white">
                          {formatCurrency(category.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Détails par catégorie */}
            <div className="card">
              <h3 className="text-lg font-semibold text-white mb-4">
                Détails par catégorie
              </h3>
              
              {categoryExpenses.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-dark-400">Aucune catégorie avec des dépenses</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {categoryExpenses
                    .sort((a, b) => b.amount - a.amount) // Trier par montant décroissant
                    .map((category) => (
                      <div key={category.id} className="flex items-center justify-between p-4 bg-dark-800 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <CategoryIconBadge
                            color={category.color}
                            icon={category.icon}
                            size={36}
                          />
                          <div>
                            <p className="text-white font-medium">{category.name}</p>
                            <p className="text-dark-400 text-sm">
                              {formatPercentage(category.percentage)} du total
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-bold">
                            {formatCurrency(category.amount)}
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Charts;
